import { useCallback, useState } from "react";
import { useTranslation } from "react-i18next";
import { useNavigate } from "react-router-dom";
import { usePassportBootstrap } from "../api/queries.js";
import { LearningRouteCompetencyDrawer } from "../components/LearningRouteCompetencyDrawer.js";
import { PassportQueryState } from "../components/PassportQueryState.js";
import { ProfileCompetencyTable } from "./ProfileCompetencyTable.js";
import { ProfileSkillsAside } from "./ProfileSkillsAside.js";
import { buildProfileSkillRows } from "./profile-skills.js";

export function ProfileSkillsTab() {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const [openPageId, setOpenPageId] = useState<string | null>(null);
  const [expandedIds, setExpandedIds] = useState<Set<string>>(() => new Set());
  const query = usePassportBootstrap(true);
  const overview = query.data?.employeeOverview;
  const catalogue = query.data?.catalogue;

  const allSkillRows = catalogue && overview ? buildProfileSkillRows(catalogue, overview.progress) : [];

  const toggleExpanded = useCallback((pageId: string) => {
    setExpandedIds((current) => {
      const next = new Set(current);
      if (next.has(pageId)) next.delete(pageId);
      else next.add(pageId);
      return next;
    });
  }, []);

  return (
    <PassportQueryState error={query.isError} loading={query.isLoading} onRetry={() => void query.refetch()}>
      {overview && catalogue ? (
        <div
          className="grid gap-5 xl:grid-cols-[minmax(0,1fr)_minmax(26rem,32rem)]"
          data-control-id="profile.skills.overview"
        >
          <div className="overflow-hidden rounded-2xl border border-border bg-background">
            <div className="border-b border-border px-5 py-4">
              <h2 className="text-base font-medium">{t("profile.tabSkills")}</h2>
            </div>
            <ProfileCompetencyTable
              expandedIds={expandedIds}
              onToggleExpanded={toggleExpanded}
              onViewCompetency={setOpenPageId}
              pages={overview.pages}
              progress={overview.progress}
              skills={allSkillRows}
            />
          </div>

          <ProfileSkillsAside
            expandedIds={expandedIds}
            onToggle={toggleExpanded}
            pages={overview.pages}
            skills={allSkillRows}
          />

          <LearningRouteCompetencyDrawer
            controlIdPrefix="profile.competency"
            onClose={() => setOpenPageId(null)}
            onSelectSkill={(_pageId, stampId) => {
              setOpenPageId(null);
              void navigate(`/me/stamps/${stampId}`);
            }}
            pageId={openPageId}
            pages={overview.pages}
            progress={overview.progress}
            resources={catalogue.learningResources}
            stamps={catalogue.stamps}
          />
        </div>
      ) : (
        <p className="text-sm text-muted-foreground">{t("profile.noSkills")}</p>
      )}
    </PassportQueryState>
  );
}
